import { ref } from "vue";
import { defineStore } from "pinia";
import {
    getCountryCodeFromTimezone,
    getCountryCodeFromIPAddress,
} from "@/helpers/timezone";
import { User } from "@/helpers/interfaces/auth";
import { generateUUID } from "@/helpers/uuid";

/**
 * Store for managing session-specific state, such as authentication status, origin, country code, language, session ID, email address, and user information. This store is designed
 * to be used across the checkout process to maintain consistency of session-related data and facilitate communication between different components and providers.
 * The store includes functions to set and update this information, and it persists the relevant data in session storage to maintain state across page reloads within the same session.
 * The country code is determined based on the user's timezone or IP address, with the ability for the user to manually select a country code if necessary.
 * The language is determined based on the user's browser settings, with a default value of "en-GB" if no language information is available.
 * This allows for a personalized and localized checkout experience for users from different regions.
 */
export const useSessionStore = defineStore(
    "session",
    () => {
        const isAuthenticated = ref<boolean>(false);
        const origin = ref<string | null>(null);
        const countryCode = ref<string | null>(null);
        const detectedCountryCode = ref<string | null>(null);
        const language = ref<string | null>(null);
        const sessionId = ref<string | null>(null);
        const emailAddress = ref<string | null>(null);
        const user = ref<User | null>(null);

        function setIsAuthenticated(value: boolean) {
            isAuthenticated.value = value;
        }

        function setOrigin(value: string | null) {
            origin.value = value;
        }

        async function setDefaultCountryCode() {
            if (countryCode.value === null || countryCode.value.length === 0) {
                countryCode.value = getCountryCodeFromTimezone();
            }

            if (countryCode.value === undefined) {
                countryCode.value = await getCountryCodeFromIPAddress();
            }

            if (countryCode.value !== undefined) {
                if (
                    countryCode.value === null ||
                    countryCode.value.length !== 2
                ) {
                    countryCode.value = await getCountryCodeFromIPAddress();
                }
            }

            if (detectedCountryCode.value === null) {
                detectedCountryCode.value = countryCode.value;
            }
        }

        function updateCountryCode(manuallySelectedCountryCode: string) {
            countryCode.value = manuallySelectedCountryCode;
        }

        function setDefaultLanguage() {
            if (!language.value) {
                language.value =
                    navigator.languages && navigator.languages.length
                        ? navigator.languages[0]
                        : navigator.language || "en-GB";
            }
        }

        function updateLanguage(manuallySelectedLanguage: string) {
            language.value = manuallySelectedLanguage;
        }

        function setSessionId(id = generateUUID()) {
            sessionId.value = id;
        }

        function setEmailAddress(email: string | null) {
            emailAddress.value = email;
        }

        function setUser(value: any) {
            user.value = value;
        }

        return {
            isAuthenticated,
            origin,
            countryCode,
            detectedCountryCode,
            language,
            sessionId,
            emailAddress,
            user,
            setIsAuthenticated,
            setOrigin,
            setDefaultCountryCode,
            updateCountryCode,
            setDefaultLanguage,
            updateLanguage,
            setSessionId,
            setEmailAddress,
            setUser,
        };
    },
    {
        persist: {
            pick: [
                "isAuthenticated",
                "origin",
                "countryCode",
                "detectedCountryCode",
                "language",
                "sessionId",
                "emailAddress",
                "user",
            ],
            storage: sessionStorage,
        },
    },
);
